<script>
  import { onMount } from "svelte";
  import api from "../../services/api.js";

  export let symbol = "";

  let stock = null;
  let quote = null;
  let loading = true;
  let error = null;
  let previousSymbol = "";

  onMount(async () => {
    previousSymbol = symbol;
    await loadStockData();
  });

  // Watch for symbol changes and reload data
  $: if (symbol && symbol !== previousSymbol) {
    previousSymbol = symbol;
    // Reset data when symbol changes
    stock = null;
    quote = null;
    loadStockData();
  }

  async function loadStockData() {
    loading = true;
    error = null;

    // Load stock info and quote separately to handle partial failures
    try {
      stock = await api.getStockInfo(symbol);
    } catch (e) {
      console.error("Error loading stock info:", e);
    }

    try {
      quote = await api.getStockQuote(symbol);
    } catch (e) {
      console.error("Error loading quote:", e);
    }

    // Only show error if both failed
    if (!stock && !quote) {
      error = "Failed to load stock information";
    }

    loading = false;
  }

  function formatCurrency(value) {
    if (value === null || value === undefined || isNaN(value)) return "N/A";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  }

  function formatNumber(value) {
    if (value === null || value === undefined || isNaN(value)) return "N/A";
    return new Intl.NumberFormat('en-US').format(value);
  }

  function formatTime(timestamp) {
    if (!timestamp) return new Date().toLocaleTimeString();
    try {
      return new Date(timestamp).toLocaleTimeString();
    } catch {
      return new Date().toLocaleTimeString();
    }
  }

  // Get the current price - prefer ask price, fall back to bid
  $: currentPrice = quote ? (quote.ap || quote.bp) : null;
  // Spread calculation
  $: spread = quote && quote.ap && quote.bp ? (quote.ap - quote.bp) : null;
</script>

{#if loading}
  <header class="mb-6">
    <div class="skeleton h-8 w-32"></div>
    <div class="skeleton h-4 w-56 mt-2"></div>
  </header>
  <div class="card card-pad mb-6">
    <div class="flex items-start justify-between gap-6">
      <div class="space-y-2">
        <div class="skeleton h-5 w-40"></div>
        <div class="skeleton h-4 w-24"></div>
      </div>
      <div class="skeleton h-10 w-36"></div>
    </div>
  </div>
  <div class="card card-pad">
    <div class="skeleton h-5 w-40 mb-4"></div>
    <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
      {#each Array(6) as _}
        <div class="skeleton h-20"></div>
      {/each}
    </div>
  </div>
{:else if error}
  <header class="mb-6">
    <h1 class="text-2xl font-bold text-ink">{symbol}</h1>
    <p class="text-sm text-ink-secondary mt-1">Stock details and live quote.</p>
  </header>
  <div class="card card-pad">
    <div class="py-10 text-center">
      <i class="fas fa-exclamation-triangle text-2xl text-ink-tertiary mb-3"></i>
      <p class="text-sm text-ink-secondary">{error}</p>
      <button
        on:click={loadStockData}
        class="btn btn-outline btn-sm mt-4"
      >
        Try Again
      </button>
    </div>
  </div>
{:else}
  <!-- Stock Header -->
  <header class="mb-6 flex flex-wrap items-start justify-between gap-4">
    <div>
      <h1 class="text-2xl font-bold text-ink">{symbol}</h1>
      <p class="text-sm text-ink-secondary mt-1">
        {stock?.name || symbol}{#if stock?.exchange}&nbsp;&middot; {stock.exchange}{/if}
      </p>
    </div>
    <div class="text-right">
      {#if quote && currentPrice}
        <p class="text-3xl font-bold text-ink tnum">
          {formatCurrency(currentPrice)}
        </p>
        {#if spread !== null}
          <p class="text-sm text-ink-secondary tnum">
            Spread: {formatCurrency(spread)}
          </p>
        {/if}
        <p class="text-xs text-ink-tertiary tnum">
          As of {formatTime(quote.t)}
        </p>
      {:else}
        <p class="text-sm text-ink-tertiary">Quote unavailable</p>
      {/if}
    </div>
  </header>

  <div class="space-y-6">
    <!-- Stock Info Card -->
    <div class="card card-pad">
      <h3 class="text-lg font-semibold text-ink mb-4">
        Stock Information
      </h3>
      <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
        {#if quote}
          <div class="rounded-xl bg-surface-variant p-4">
            <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Ask Price</p>
            <p class="text-xl font-semibold text-ink tnum mt-1">{formatCurrency(quote.ap)}</p>
          </div>
          <div class="rounded-xl bg-surface-variant p-4">
            <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Bid Price</p>
            <p class="text-xl font-semibold text-ink tnum mt-1">{formatCurrency(quote.bp)}</p>
          </div>
          <div class="rounded-xl bg-surface-variant p-4">
            <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Ask Size</p>
            <p class="text-xl font-semibold text-ink tnum mt-1">{formatNumber(quote.as)}</p>
          </div>
          <div class="rounded-xl bg-surface-variant p-4">
            <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Bid Size</p>
            <p class="text-xl font-semibold text-ink tnum mt-1">{formatNumber(quote.bs)}</p>
          </div>
        {/if}
        {#if stock}
          <div class="rounded-xl bg-surface-variant p-4">
            <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Exchange</p>
            <p class="text-xl font-semibold text-ink mt-1">{stock.exchange || 'N/A'}</p>
          </div>
          <div class="rounded-xl bg-surface-variant p-4">
            <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Asset Class</p>
            <p class="text-xl font-semibold text-ink mt-1">{stock.asset_class || 'Equity'}</p>
          </div>
          <div class="rounded-xl bg-surface-variant p-4">
            <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Tradable</p>
            <p class="text-xl font-semibold mt-1 {stock.tradable ? 'text-emerald-600' : 'text-red-600'}">
              {stock.tradable ? 'Yes' : 'No'}
            </p>
          </div>
          <div class="rounded-xl bg-surface-variant p-4">
            <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Fractionable</p>
            <p class="text-xl font-semibold mt-1 {stock.fractionable ? 'text-emerald-600' : 'text-red-600'}">
              {stock.fractionable ? 'Yes' : 'No'}
            </p>
          </div>
          <div class="rounded-xl bg-surface-variant p-4">
            <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Shortable</p>
            <p class="text-xl font-semibold mt-1 {stock.shortable ? 'text-emerald-600' : 'text-red-600'}">
              {stock.shortable ? 'Yes' : 'No'}
            </p>
          </div>
          <div class="rounded-xl bg-surface-variant p-4">
            <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Marginable</p>
            <p class="text-xl font-semibold mt-1 {stock.marginable ? 'text-emerald-600' : 'text-red-600'}">
              {stock.marginable ? 'Yes' : 'No'}
            </p>
          </div>
        {/if}
      </div>
    </div>

    <!-- Info Note -->
    <div class="banner banner-info">
      <i class="fas fa-info-circle mr-2"></i>
      To invest in this stock, add it to one of your <a href="/admin/pies" class="font-semibold underline hover:no-underline">Pies</a>.
      Moira Finance uses Pies to automatically allocate your investments based on your target percentages.
    </div>
  </div>
{/if}
